/**
 * TypeNormalizer - Centralized type normalization service
 *
 * Handles both consequence types AND mention types.
 *
 * Solves the critical issue where types are stored/referenced in multiple formats:
 * - Database: 'friday-detention' (value field - canonical)
 * - UI: 'Friday Detentions' (label field - display)
 * - Singular: 'Friday detention' (singular field)
 * - Plural: 'Friday detentions' (plural field)
 *
 * This service provides a SINGLE SOURCE OF TRUTH for normalizing any variant
 * to the canonical storage format (value field).
 */

import { consequenceTypesService } from '../../consequenceTypesService.js';
import { mentionTypesService } from '../../mentionTypesService.js';
import { disciplineLogger } from '../../../utils/disciplineLogger.js';

export class TypeNormalizer {
  constructor() {
    this.lookupMap = null;
    this.mentionLookupMap = null;
    this.cacheTimestamp = null;
    this.mentionCacheTimestamp = null;
    this.cacheDuration = 5 * 60 * 1000; // 5 minutes (matches service caches)
  }

  /**
   * Check if cache is valid
   * @param {string} cacheType - 'consequence' or 'mention'
   * @returns {boolean}
   */
  isCacheValid(cacheType = 'consequence') {
    if (cacheType === 'mention') {
      if (!this.mentionLookupMap || !this.mentionCacheTimestamp) return false;
      return Date.now() - this.mentionCacheTimestamp < this.cacheDuration;
    }
    if (!this.lookupMap || !this.cacheTimestamp) return false;
    return Date.now() - this.cacheTimestamp < this.cacheDuration;
  }

  /**
   * Build lookup map from all consequence type variants
   * @param {string} schoolId - School ID for fetching consequence types
   * @returns {Promise<Map>}
   */
  async buildLookupMap(schoolId) {
    try {
      const types = await consequenceTypesService.getAll(schoolId);
      const lookupMap = new Map();

      for (const type of types) {
        const canonicalValue = type.value;

        // Map all variants to canonical value
        if (type.value) {
          lookupMap.set(type.value.toLowerCase(), canonicalValue);
        }
        if (type.label) {
          lookupMap.set(type.label.toLowerCase(), canonicalValue);
        }
        if (type.singular) {
          lookupMap.set(type.singular.toLowerCase(), canonicalValue);
        }
        if (type.plural) {
          lookupMap.set(type.plural.toLowerCase(), canonicalValue);
        }

        // Also handle without spaces/hyphens for flexibility
        if (type.value) {
          const noHyphens = type.value.replace(/-/g, ' ').toLowerCase();
          const noSpaces = type.value.replace(/ /g, '-').toLowerCase();
          lookupMap.set(noHyphens, canonicalValue);
          lookupMap.set(noSpaces, canonicalValue);
        }
      }

      return lookupMap;
    } catch (error) {
      console.error('Error building type lookup map:', error);
      // Return empty map on error - fallback logic will handle it
      return new Map();
    }
  }

  /**
   * Build lookup map from all mention type variants
   * @param {string} schoolId - School ID for fetching mention types
   * @returns {Promise<Map>}
   */
  async buildMentionLookupMap(schoolId) {
    try {
      const types = await mentionTypesService.getAll(schoolId);
      const lookupMap = new Map();

      for (const type of types) {
        const canonicalValue = type.value;

        // Map all variants to canonical value
        if (type.value) {
          lookupMap.set(type.value.toLowerCase(), canonicalValue);
        }
        if (type.label) {
          lookupMap.set(type.label.toLowerCase(), canonicalValue);
        }
        if (type.singular) {
          lookupMap.set(type.singular.toLowerCase(), canonicalValue);
        }
        if (type.plural) {
          lookupMap.set(type.plural.toLowerCase(), canonicalValue);
        }

        // Also handle without spaces/hyphens for flexibility
        if (type.value) {
          const noHyphens = type.value.replace(/-/g, ' ').toLowerCase();
          const noSpaces = type.value.replace(/ /g, '-').toLowerCase();
          lookupMap.set(noHyphens, canonicalValue);
          lookupMap.set(noSpaces, canonicalValue);
        }
      }

      return lookupMap;
    } catch (error) {
      console.error('Error building mention type lookup map:', error);
      // Return empty map on error - fallback logic will handle it
      return new Map();
    }
  }

  /**
   * Get or build cached lookup map
   * @param {string} cacheType - 'consequence' or 'mention'
   * @param {string} schoolId - School ID for fetching consequence types
   * @returns {Promise<Map>}
   */
  async getLookupMap(cacheType = 'consequence', schoolId) {
    if (cacheType === 'mention') {
      if (this.isCacheValid('mention')) {
        return this.mentionLookupMap;
      }
      this.mentionLookupMap = await this.buildMentionLookupMap(schoolId);
      this.mentionCacheTimestamp = Date.now();
      return this.mentionLookupMap;
    }

    if (this.isCacheValid('consequence')) {
      return this.lookupMap;
    }

    this.lookupMap = await this.buildLookupMap(schoolId);
    this.cacheTimestamp = Date.now();
    return this.lookupMap;
  }

  /**
   * Clear the cache (useful after types change)
   * @param {string} cacheType - 'consequence', 'mention', or 'all'
   */
  clearCache(cacheType = 'all') {
    if (cacheType === 'all' || cacheType === 'consequence') {
      this.lookupMap = null;
      this.cacheTimestamp = null;
    }
    if (cacheType === 'all' || cacheType === 'mention') {
      this.mentionLookupMap = null;
      this.mentionCacheTimestamp = null;
    }
  }

/**
   * Normalize type to canonical value (from consequenceType.value field)
   * @param {string} input - Type string to normalize
   * @param {string} schoolId - School ID for fetching consequence types
   * @returns {Promise<string>} Canonical value (from consequenceType.value field)
   */
  async normalize(input, schoolId) {
    // Handle null/undefined/empty
    if (!input || typeof input !== 'string') {
      disciplineLogger.debug('TypeNormalizer: null/undefined/empty input', { input });
      return input;
    }

    const trimmedInput = input.trim();
    if (trimmedInput === '') {
      return input;
    }

    // Try lookup map first (fast path)
    const lookupMap = await this.getLookupMap('consequence', schoolId);
    const normalized = lookupMap.get(trimmedInput.toLowerCase());

    if (normalized) {
      if (normalized !== trimmedInput) {
        disciplineLogger.debug('TypeNormalizer: normalized', {
          input: trimmedInput,
          output: normalized
        });
      }
      return normalized;
    }

    // Fallback: Pattern matching for common cases
    const fallback = this.fallbackNormalization(trimmedInput);
    if (fallback !== trimmedInput) {
      disciplineLogger.debug('TypeNormalizer: fallback normalization', {
        input: trimmedInput,
        output: fallback,
        reason: 'Not found in lookup map'
      });
      return fallback;
    }

    // If still no match, log warning and return as-is
    disciplineLogger.debug('TypeNormalizer: unknown type, returning as-is', {
      input: trimmedInput,
      reason: 'Not found in lookup map or fallback patterns'
    });

    return trimmedInput;
  }

  /**
   * Fallback normalization using string patterns
   * Used when type isn't found in Firestore (e.g., legacy data, typos)
   * @param {string} input - Input type
   * @returns {string} Best-guess normalized type
   */
  fallbackNormalization(input) {
    const lower = input.toLowerCase().trim();

    // Handle explicit plural → singular for common types
    const pluralMap = {
      'demerits': 'demerit',
      'warnings': 'warning',
      'detentions': 'detention',
      'suspensions': 'suspension',
      'referrals': 'referral',
      'mentions': 'mention',
      'friday detentions': 'friday-detention',
      'saturday detentions': 'saturday-detention',
      'homework detentions': 'homework-detention',
      'privilege losses': 'privilege-loss',
      'office referrals': 'referral',
      'behavior contracts': 'contract',
      'restorative actions': 'restorative'
    };

    if (pluralMap[lower]) {
      return pluralMap[lower];
    }

    // Pattern matching for compound types
    if (lower.includes('friday') && lower.includes('detention')) {
      return 'friday-detention';
    }
    if (lower.includes('saturday') && lower.includes('detention')) {
      return 'saturday-detention';
    }
    if (lower.includes('homework') && lower.includes('detention')) {
      return 'homework-detention';
    }
    if (lower.includes('detention')) {
      return 'detention';
    }
    if (lower.includes('suspension')) {
      return 'suspension';
    }
    if (lower.includes('privilege') && lower.includes('loss')) {
      return 'privilege-loss';
    }

    // Generic plural stripping (last resort)
    // Only strip 's' if it's not part of the base word
    if (lower.endsWith('s') && lower.length > 2 && !lower.endsWith('ss')) {
      // Don't strip from words ending in 'ss' (class, pass, etc.)
      const withoutS = lower.slice(0, -1);
      disciplineLogger.debug('TypeNormalizer: stripped plural', {
        input,
        output: withoutS
      });
      return withoutS;
    }

    return input;
  }

/**
   * Normalize many types at once (for efficiency)
   * @param {Array<string>} inputs - Types to normalize
   * @param {string} schoolId - School ID for fetching consequence types
   * @returns {Promise<Map<string, string>>} Map of input → normalized
   */
  async normalizeMany(inputs, schoolId) {
    const result = new Map();

    // Ensure lookup map is loaded once
    await this.getLookupMap('consequence', schoolId);

    for (const input of inputs) {
      const normalized = await this.normalize(input, schoolId);
      result.set(input, normalized);
    }

    return result;
  }

  /**
   * Check if a type exists in the consequence types registry
   * @param {string} input - Type to check
   * @param {string} schoolId - School ID for fetching consequence types
   * @returns {Promise<boolean>} True if type exists
   */
  async exists(input, schoolId) {
    const normalized = await this.normalize(input, schoolId);
    const lookupMap = await this.getLookupMap('consequence', schoolId);

    // Check if normalized value exists as a canonical value
    for (const [key, value] of lookupMap.entries()) {
      if (value === normalized) {
        return true;
      }
    }

    return false;
  }

  /**
   * Get all valid canonical consequence types
   * @param {string} schoolId - School ID for fetching consequence types
   * @returns {Promise<Array<string>>} Array of canonical values
   */
  async getValidTypes(schoolId) {
    const types = await consequenceTypesService.getAll(schoolId);
    return types.map(t => t.value);
  }

  /**
   * Validate that a type is valid
   * @param {string} input - Type to validate
   * @param {string} schoolId - School ID for fetching consequence types
   * @returns {Promise<{valid: boolean, normalized: string, message: string}>}
   */
  async validate(input, schoolId) {
    const normalized = await this.normalize(input, schoolId);
    const validTypes = await this.getValidTypes(schoolId);
    const valid = validTypes.includes(normalized);

    return {
      valid,
      normalized,
      message: valid
        ? `Valid type: ${normalized}`
        : `Invalid type: "${input}" (normalized to "${normalized}"). Valid types: ${validTypes.join(', ')}`
    };
  }

  /**
   * Normalize type to Firestore document ID
   * Accepts either a type string (value/label/variant) or an existing ID
   * @param {string} input - Type string or ID
   * @param {string} schoolId - School ID for fetching consequence types
   * @returns {Promise<string|null>} Firestore document ID or null
   */
  async normalizeToId(input, schoolId) {
    if (!input) return null;

    // Check if input is already a valid ID (Firestore IDs are typically 20 chars alphanumeric)
    // First try to get by ID to see if it's already an ID
    const typeById = await consequenceTypesService.getById(input, schoolId);
    if (typeById) {
      return typeById.id;
    }

    // Not an ID, so normalize the string to canonical value, then get ID
    const normalized = await this.normalize(input, schoolId);
    const idByValue = await consequenceTypesService.getIdByValue(normalized, schoolId);

    if (!idByValue) {
      disciplineLogger.debug('TypeNormalizer: could not find ID for type', {
        input,
        normalized,
        reason: 'Type not found in registry'
      });
    }

    return idByValue;
  }

/**
   * Get full consequence type document from any input
   * Accepts value, label, plural, singular, or ID
   * @param {string} input - Type string or ID
   * @param {string} schoolId - School ID for fetching consequence types
   * @returns {Promise<Object|null>} Type document with all fields
   */
  async getTypeDocument(input, schoolId) {
    if (!input) return null;

    // Try as ID first (fast path)
    const typeById = await consequenceTypesService.getById(input, schoolId);
    if (typeById) {
      return typeById;
    }

    // Fall back to value lookup (slower path)
    const normalized = await this.normalize(input, schoolId);
    const typeByValue = await consequenceTypesService.getByValue(normalized, schoolId);

    if (!typeByValue) {
      disciplineLogger.debug('TypeNormalizer: could not find consequence type document', {
        input,
        normalized,
        reason: 'Consequence type not found in registry'
      });
    }

    return typeByValue;
  }

  /**
   * Batch normalize multiple types to IDs
   * @param {Array<string>} inputs - Array of type strings or IDs
   * @param {string} schoolId - School ID for fetching consequence types
   * @returns {Promise<Map<string, string|null>>} Map of input → ID
   */
  async normalizeToIds(inputs, schoolId) {
    const result = new Map();

    for (const input of inputs) {
      const id = await this.normalizeToId(input, schoolId);
      result.set(input, id);
    }

    return result;
  }

/**
   * Normalize mention type to canonical value (from mentionType.value field)
   * @param {string} input - Mention type string to normalize
   * @param {string} schoolId - School ID for fetching mention types
   * @returns {Promise<string>} Canonical value (from mentionType.value field)
   */
  async normalizeMentionType(input, schoolId) {
    // Handle null/undefined/empty
    if (!input || typeof input !== 'string') {
      disciplineLogger.debug('TypeNormalizer: null/undefined/empty mention type input', { input });
      return input;
    }

    const trimmedInput = input.trim();
    if (trimmedInput === '') {
      return input;
    }

    // Try lookup map first (fast path)
    const lookupMap = await this.getLookupMap('mention', schoolId);
    const normalized = lookupMap.get(trimmedInput.toLowerCase());

    if (normalized) {
      if (normalized !== trimmedInput) {
        disciplineLogger.debug('TypeNormalizer: normalized mention type', {
          input: trimmedInput,
          output: normalized
        });
      }
      return normalized;
    }

    // If not found, return as-is
    disciplineLogger.debug('TypeNormalizer: unknown mention type, returning as-is', {
      input: trimmedInput,
      reason: 'Not found in mention type lookup map'
    });

    return trimmedInput;
  }

  /**
   * Normalize mention type to Firestore document ID
   * Accepts either a mention type string (value/label/variant) or an existing ID
   * @param {string} input - Mention type string or ID
   * @param {string} schoolId - School ID for fetching mention types
   * @returns {Promise<string|null>} Firestore document ID or null
   */
  async normalizeMentionTypeToId(input, schoolId) {
    if (!input) return null;

    // Check if input is already a valid ID
    const typeById = await mentionTypesService.getById(input, schoolId);
    if (typeById) {
      return typeById;
    }

    // Not an ID, so normalize the string to canonical value, then get ID
    const normalized = await this.normalizeMentionType(input, schoolId);
    const idByValue = await mentionTypesService.getIdByValue(normalized, schoolId);
    
    if (!idByValue) {
      disciplineLogger.debug('TypeNormalizer: could not find ID for mention type', {
        input,
        normalized,
        reason: 'Mention type not found in registry'
      });
    }
    
    return idByValue;
  }
}

// Export singleton instance
export const typeNormalizer = new TypeNormalizer();
export default typeNormalizer;
